/**
 * Date formatting helpers shared across tracking and lookup views.
 *
 * EasyPost stores carrier-local times with a Z suffix even though the values
 * are already in the carrier's local timezone. The "WithoutTzConversion"
 * helpers extract the time components from the ISO string directly so we
 * don't shift by the viewer's offset.
 */

import { format } from 'date-fns';

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Parse an ISO string that uses a Z suffix to indicate carrier-local time
 * (rather than true UTC), returning a Date interpreted as local.
 */
export function parseCarrierLocalIso(isoString: string): Date {
  const normalized = isoString.endsWith('Z') ? isoString.slice(0, -1) : isoString;
  return new Date(normalized);
}

/**
 * Extract a 12-hour clock time (e.g. "6:20 PM") from an ISO string without
 * applying any timezone shift.
 */
export function formatTimeWithoutTzConversion(isoString: string): string {
  const match = isoString.match(/T(\d{2}):(\d{2})/);
  if (!match) return '';
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
}

/**
 * Extract a short month/day label (e.g. "Dec 16") from an ISO string without
 * applying any timezone shift.
 */
export function formatDateWithoutTzConversion(isoString: string): string {
  const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const month = MONTHS_SHORT[parseInt(match[2], 10) - 1];
  const day = parseInt(match[3], 10);
  return `${month} ${day}`;
}

/**
 * Format a date using date-fns. Wrapper kept for parity with the other
 * helpers and to provide a single import surface.
 */
export function formatDate(value: Date | string, pattern: string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return format(date, pattern);
}
